/**
 * Content-intelligence SSE pipeline → store subscriber.
 * Migrated in Phase 8 commit 78.
 *
 * Owns the `contentIntelligenceSubPhases` state machine — `buildSubPhases`
 * is mirrored locally so the runner stays a thin event publisher.
 */

import { sseEvents } from "./sseEvents";
import { toThoughtEvent } from "../store/department/thoughtStream";
import { CONTENT_INSPIRATION_STEP_INDEX } from "../store/department/pipelines/ecommerceHighlights";
import type {
  CampaignData,
  CoAgentMessage,
  StepStatus,
  ThoughtEvent,
} from "../store/department/types";

interface ContentIntelligenceSubscriberHooks {
  addStepLog: (stepIndex: number, text: string) => void;
  addThoughtEvent: (stepIndex: number, event: Omit<ThoughtEvent, "id" | "ts">) => void;
  addMessage: (msg: Omit<CoAgentMessage, "id" | "timestamp">) => void;
  updateCampaignData: (data: Partial<CampaignData>) => void;
  setStepState: (
    updater: (state: {
      currentStepIndex: number;
      stepStatuses: Record<number, StepStatus>;
    }) => Partial<{ currentStepIndex: number; stepStatuses: Record<number, StepStatus> }>,
  ) => void;
  setFocusedStep: (index: number) => void;
}

const CI_PHASES = [
  { phase: "keyword_generation", label: "Generating keywords" },
  { phase: "meta_discovery", label: "Discovering Meta ads" },
  { phase: "tiktok_discovery", label: "Discovering TikTok videos" },
  { phase: "video_analysis", label: "Analyzing top videos" },
  { phase: "content_synthesis", label: "Generating content briefs" },
] as const;

function buildSubPhases(activePhase: string, activeStatus: "running" | "complete" | "error") {
  const idx = CI_PHASES.findIndex((p) => p.phase === activePhase);
  return CI_PHASES.map((p, i) => ({
    phase: p.phase,
    label: p.label,
    status: i < idx ? ("complete" as const) : i === idx ? activeStatus : ("pending" as const),
  }));
}

let wired = false;

export function wireContentIntelligenceSubscriber(
  getHooks: () => ContentIntelligenceSubscriberHooks,
): void {
  if (wired) return;
  wired = true;

  const stepIndex = CONTENT_INSPIRATION_STEP_INDEX;

  const thought = (
    phase: string,
    status: "running" | "complete" | "error",
    data?: Record<string, unknown>,
    progress?: Record<string, unknown>,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ev = toThoughtEvent("content-intelligence", phase, status, data as any, progress as any);
    if (ev) getHooks().addThoughtEvent(stepIndex, ev);
  };

  sseEvents.subscribe("content-intelligence:keyword-generation-running", () => {
    const hooks = getHooks();
    thought("keyword_generation", "running");
    hooks.updateCampaignData({
      contentIntelligenceStatus: "running",
      contentIntelligencePhase: "keyword_generation",
      contentIntelligenceSubPhases: buildSubPhases("keyword_generation", "running"),
    });
    hooks.addStepLog(
      stepIndex,
      "Generating platform-specific search keywords for your products...",
    );
  });

  sseEvents.subscribe("content-intelligence:keyword-generation-complete", ({ data }) => {
    const hooks = getHooks();
    thought("keyword_generation", "complete", data);
    const productKeywords = (data.productKeywords ?? []) as CampaignData["productKeywords"];
    hooks.updateCampaignData({
      contentIntelligencePhase: "meta_discovery",
      contentIntelligenceSubPhases: buildSubPhases("keyword_generation", "complete"),
      productKeywords,
    });
    hooks.addStepLog(
      stepIndex,
      `[SUCCESS] Keywords generated for ${Array.isArray(productKeywords) ? productKeywords.length : 0} products`,
    );
  });

  sseEvents.subscribe("content-intelligence:meta-discovery-running", () => {
    const hooks = getHooks();
    thought("meta_discovery", "running");
    hooks.updateCampaignData({
      contentIntelligencePhase: "meta_discovery",
      contentIntelligenceSubPhases: buildSubPhases("meta_discovery", "running"),
    });
    hooks.addStepLog(stepIndex, "Discovering top Meta ads and creative formats...");
  });

  sseEvents.subscribe("content-intelligence:meta-discovery-complete", ({ data }) => {
    const hooks = getHooks();
    thought("meta_discovery", "complete", data);
    const metaTopAds = (data.metaTopAds ?? []) as CampaignData["metaTopAds"];
    const metaInsights =
      (data.metaInsights ?? {
        adCount: 0,
        creativeTypes: [],
        ctaTypes: [],
        topPages: [],
        publisherPlatforms: [],
      }) as CampaignData["metaInsights"];
    const metaRecommendations =
      (data.metaRecommendations ?? []) as CampaignData["metaRecommendations"];
    hooks.updateCampaignData({
      contentIntelligencePhase: "tiktok_discovery",
      contentIntelligenceSubPhases: buildSubPhases("meta_discovery", "complete"),
      metaTopAds,
      metaInsights,
      metaRecommendations,
    });
    hooks.addStepLog(
      stepIndex,
      `[SUCCESS] Found ${Array.isArray(metaTopAds) ? metaTopAds.length : 0} Meta ads with format/CTA insights`,
    );
    const recs = data.metaRecommendations as
      | Array<{ creativeType?: string; confidence?: string }>
      | undefined;
    if (recs?.[0]?.creativeType) {
      hooks.addStepLog(
        stepIndex,
        `Top-performing Meta ad type: ${recs[0].creativeType} (${recs[0].confidence} confidence)`,
      );
    }
  });

  sseEvents.subscribe("content-intelligence:tiktok-discovery-running", () => {
    const hooks = getHooks();
    thought("tiktok_discovery", "running");
    hooks.updateCampaignData({
      contentIntelligencePhase: "tiktok_discovery",
      contentIntelligenceSubPhases: buildSubPhases("tiktok_discovery", "running"),
    });
    hooks.addStepLog(stepIndex, "Discovering top TikTok videos by hashtag...");
  });

  sseEvents.subscribe("content-intelligence:tiktok-discovery-complete", ({ data }) => {
    const hooks = getHooks();
    thought("tiktok_discovery", "complete", data);
    const tiktokTopAds = (data.topAds ?? []) as CampaignData["tiktokTopAds"];
    const tiktokTrending =
      (data.trending ?? {
        hashtags: [],
        trendingSearches: [],
        trendingMusic: [],
      }) as CampaignData["tiktokTrending"];
    hooks.updateCampaignData({
      contentIntelligencePhase: "video_analysis",
      contentIntelligenceSubPhases: buildSubPhases("tiktok_discovery", "complete"),
      tiktokTopAds,
      tiktokTrending,
    });
    hooks.addStepLog(
      stepIndex,
      `[SUCCESS] Found ${Array.isArray(tiktokTopAds) ? tiktokTopAds.length : 0} top videos + trending content`,
    );
  });

  sseEvents.subscribe("content-intelligence:video-analysis-running", ({ data, progress }) => {
    const hooks = getHooks();
    thought("video_analysis", "running", data, progress);
    hooks.updateCampaignData({
      contentIntelligencePhase: "video_analysis",
      contentIntelligenceSubPhases: buildSubPhases("video_analysis", "running"),
    });
    const analyzed = data.analyzed;
    const total = data.total;
    if (analyzed != null && total != null) {
      hooks.addStepLog(stepIndex, `Analyzing video ${analyzed}/${total} with Gemini...`);
    } else {
      hooks.addStepLog(stepIndex, "Analyzing top videos with Gemini AI...");
    }
  });

  sseEvents.subscribe("content-intelligence:video-analysis-complete", ({ data }) => {
    const hooks = getHooks();
    thought("video_analysis", "complete", data);
    const videoAnalyses = (data.videoAnalyses ?? []) as CampaignData["videoAnalyses"];
    hooks.updateCampaignData({
      contentIntelligencePhase: "content_synthesis",
      contentIntelligenceSubPhases: buildSubPhases("video_analysis", "complete"),
      videoAnalyses,
    });
    hooks.addStepLog(
      stepIndex,
      `[SUCCESS] ${Array.isArray(videoAnalyses) ? videoAnalyses.length : 0} videos analyzed — hooks, pacing, visual styles extracted`,
    );
  });

  sseEvents.subscribe("content-intelligence:content-synthesis-running", () => {
    const hooks = getHooks();
    thought("content_synthesis", "running");
    hooks.updateCampaignData({
      contentIntelligencePhase: "content_synthesis",
      contentIntelligenceSubPhases: buildSubPhases("content_synthesis", "running"),
    });
    hooks.addStepLog(
      stepIndex,
      "Generating content briefs with scripts, visual directions, and A/B test plans...",
    );
  });

  sseEvents.subscribe("content-intelligence:content-synthesis-complete", ({ data }) => {
    const hooks = getHooks();
    thought("content_synthesis", "complete", data);
    const contentBriefs = (data.contentBriefs ?? []) as CampaignData["contentBriefs"];
    hooks.updateCampaignData({
      contentIntelligencePhase: "content_synthesis",
      contentIntelligenceSubPhases: buildSubPhases("content_synthesis", "complete"),
      contentBriefs,
    });
    hooks.addStepLog(
      stepIndex,
      `[SUCCESS] ${Array.isArray(contentBriefs) ? contentBriefs.length : 0} content briefs generated`,
    );
  });

  sseEvents.subscribe("content-intelligence:done", ({ data }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      contentIntelligenceStatus: "complete",
      contentIntelligencePhase: "done",
      contentIntelligenceSubPhases: undefined,
      productKeywords: data.productKeywords as CampaignData["productKeywords"],
      metaTopAds: data.metaTopAds as CampaignData["metaTopAds"],
      metaInsights: data.metaInsights as CampaignData["metaInsights"],
      metaRecommendations: data.metaRecommendations as CampaignData["metaRecommendations"],
      tiktokTopAds: data.topAds as CampaignData["tiktokTopAds"],
      tiktokTrending: data.trending as CampaignData["tiktokTrending"],
      videoAnalyses: data.videoAnalyses as CampaignData["videoAnalyses"],
      contentBriefs: data.contentBriefs as CampaignData["contentBriefs"],
    });
    hooks.setStepState((state) => ({
      currentStepIndex: stepIndex,
      stepStatuses: { ...state.stepStatuses, [stepIndex]: "waiting" },
    }));
    hooks.setFocusedStep(stepIndex);
    const metaTopAds = data.metaTopAds as unknown[] | undefined;
    const tiktokTopAds = data.topAds as unknown[] | undefined;
    const videoAnalyses = data.videoAnalyses as unknown[] | undefined;
    const contentBriefs = data.contentBriefs as unknown[] | undefined;
    hooks.addMessage({
      role: "agent",
      content: `Content intelligence analysis complete! Found ${metaTopAds?.length ?? 0} Meta ads and ${tiktokTopAds?.length ?? 0} TikTok videos, analyzed ${videoAnalyses?.length ?? 0} with Gemini, and generated ${contentBriefs?.length ?? 0} content briefs with scripts ready for production.`,
    });
  });

  sseEvents.subscribe("content-intelligence:error", ({ message, failedPhase }) => {
    const hooks = getHooks();
    thought(failedPhase ?? "error", "error", { message });
    hooks.updateCampaignData({
      contentIntelligenceStatus: "error",
      contentIntelligencePhase: "error",
      contentIntelligenceSubPhases: failedPhase
        ? buildSubPhases(failedPhase, "error")
        : undefined,
    });
    hooks.addStepLog(stepIndex, `[ERROR] ${message}`);
    hooks.addMessage({
      role: "agent",
      content: `Content intelligence encountered an error: ${message}`,
    });
  });
}
